import { BulbOutlined, ExperimentOutlined, ReloadOutlined, SendOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Cascader, Descriptions, Drawer, Flex, Form, InputNumber, Select, Space, Switch, Table, Typography, Upload } from "antd";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { PlatformLabel } from "../channels/PlatformBadge";
import { ChannelStatusTag, ProductImage, type Product } from "./Products";

type Channel = Product["channels"][number];
type Category = Schemas["MarketplaceCategory"];
type Store = { id: string; name: string; platform: Schemas["ChannelPlatformName"] };

type CascaderOption = { value: string; label: string; children?: CascaderOption[]; disabled?: boolean; isLeaf?: boolean };

/** The flat category list as a tree for the cascader (leaves are where products go). */
function categoryTree(cats: Category[]): CascaderOption[] {
  const kids = new Map<string, Category[]>();
  for (const c of cats) {
    const list = kids.get(c.parentId) ?? [];
    list.push(c);
    kids.set(c.parentId, list);
  }
  const build = (parent: string): CascaderOption[] =>
    (kids.get(parent) ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      disabled: !c.available,
      isLeaf: c.leaf,
      ...(c.leaf ? {} : { children: build(c.id) }),
    }));
  return build("");
}

function pathTo(cats: Category[], id: string): Category[] {
  const by = new Map(cats.map((c) => [c.id, c]));
  const out: Category[] = [];
  for (let c = by.get(id); c; c = c.parentId ? by.get(c.parentId) : undefined) out.unshift(c);
  return out;
}

type Values = {
  category?: string[];
  brandId?: string;
  attrs?: Record<string, string[] | string | undefined>;
  prices?: Record<string, number | null | undefined>;
  cod?: boolean;
};

/**
 * Atur & publikasikan: the product in one store's terms (category,
 * attributes, brand, size chart, prices), saved with the product and sent
 * when published. What the category requires is checked before sending.
 */
export function StoreDrawer({ product, store, onClose, onFix }: {
  product: Product;
  store: Store | null;
  onClose: () => void;
  // Opens the AI writer to answer the marketplace's correction notes.
  onFix?: (connectionId: string) => void;
}) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const [form] = Form.useForm<Values>();
  const [missing, setMissing] = useState<Record<string, string> | null>(null);
  const [brandQ, setBrandQ] = useState("");
  const brandSearch = useDeferredValue(brandQ.trim());
  const connectionID = store?.id ?? "";
  const ch: Channel | undefined = product.channels.find((c) => c.connectionId === connectionID);
  const path = { params: { path: { connectionID } } };
  const categories = useQuery({
    queryKey: ["products", "categories", connectionID],
    queryFn: () => unwrap(api.GET("/v1/products/connections/{connectionID}/categories", path)),
    enabled: !!store,
    staleTime: 3600_000,
  });
  const cats = useMemo(() => categories.data?.items ?? [], [categories.data]);
  const tree = useMemo(() => categoryTree(cats), [cats]);
  const picked = Form.useWatch("category", form);
  const categoryId = picked?.[picked.length - 1] ?? "";
  const terms = useQuery({
    queryKey: ["products", "terms", connectionID, categoryId],
    queryFn: () => unwrap(api.GET("/v1/products/connections/{connectionID}/categories/{categoryID}", { params: { path: { connectionID, categoryID: categoryId } } })),
    enabled: !!store && !!categoryId && cats.find((c) => c.id === categoryId)?.leaf === true,
    staleTime: 3600_000,
  });
  const brands = useQuery({
    queryKey: ["products", "brands", connectionID, categoryId, brandSearch],
    queryFn: () =>
      unwrap(api.GET("/v1/products/connections/{connectionID}/brands", { params: { path: { connectionID }, query: { categoryId, ...(brandSearch ? { q: brandSearch } : {}) } } })),
    enabled: !!store && !!categoryId,
    staleTime: 600_000,
  });

  // The saved profile into the form once the tree is there.
  useEffect(() => {
    if (!store || cats.length === 0) return;
    const attrs: Values["attrs"] = {};
    for (const a of ch?.attributes ?? []) attrs[a.id] = [...(a.valueIds ?? []), ...(a.custom ?? [])];
    form.setFieldsValue({
      category: ch?.categoryId ? pathTo(cats, ch.categoryId).map((c) => c.id) : undefined,
      brandId: ch?.brandId || undefined,
      attrs,
      prices: Object.fromEntries(Object.entries(ch?.prices ?? {}).map(([k, v]) => [k, v])),
      cod: ch?.cod ?? true,
    });
  }, [store, cats, ch, form]);

  const recommend = useMutation({
    mutationFn: () => unwrap(api.GET("/v1/products/connections/{connectionID}/categories/recommend", { params: { path: { connectionID }, query: { title: product.name } } })),
    onSuccess: (r) => {
      if (r.categoryId) form.setFieldValue("category", pathTo(cats, r.categoryId).map((c) => c.id));
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const body = (v: Values) => {
    const attrDefs = terms.data?.attributes ?? [];
    const attributes = attrDefs
      .filter((a) => !a.sales)
      .map((a) => {
        const raw = v.attrs?.[a.id];
        const chosen = (Array.isArray(raw) ? raw : raw ? [raw] : []).filter(Boolean);
        const ids = chosen.filter((x) => a.values.some((val) => val.id === x));
        const custom = chosen.filter((x) => !a.values.some((val) => val.id === x));
        return { id: a.id, name: a.name, valueIds: ids, custom, valueNames: [...ids.map((id) => a.values.find((val) => val.id === id)!.name), ...custom] };
      })
      .filter((a) => a.valueIds.length + a.custom.length > 0);
    const prices: Record<string, number> = {};
    for (const [k, x] of Object.entries(v.prices ?? {})) if (x) prices[k] = x;
    const brand = brands.data?.items.find((b) => b.id === v.brandId);
    return {
      categoryId,
      categoryName: pathTo(cats, categoryId)
        .map((c) => c.name)
        .join(" › "),
      brandId: v.brandId ?? "",
      brandName: brand?.name ?? (v.brandId === ch?.brandId ? (ch?.brandName ?? "") : ""),
      attributes,
      prices,
      cod: v.cod ?? true,
      sizeChartId: product.images.find((i) => i.role === "size_chart")?.id ?? null,
    };
  };
  const done = (p: Product) => {
    queryClient.setQueryData(["products", "detail", product.id], p);
    void queryClient.invalidateQueries({ queryKey: ["products", "list"] });
  };
  const save = useMutation({
    mutationFn: (v: Values) => unwrap(api.PUT("/v1/products/{productID}/channels/{connectionID}", { params: { path: { productID: product.id, connectionID } }, body: body(v) })),
    onSuccess: (p) => (done(p), void message.success(t("prod.store.saved"))),
    onError: (err) => void message.error(errorText(err)),
  });
  const publish = useMutation({
    mutationFn: async (v: Values) => {
      setMissing(null);
      await unwrap(api.PUT("/v1/products/{productID}/channels/{connectionID}", { params: { path: { productID: product.id, connectionID } }, body: body(v) }));
      return unwrap(api.POST("/v1/products/{productID}/channels/{connectionID}/push", { params: { path: { productID: product.id, connectionID } }, body: {} }));
    },
    onSuccess: (p) => (done(p), void message.success(t("prod.store.published"))),
    onError: (err) => {
      void queryClient.invalidateQueries({ queryKey: ["products", "detail", product.id] });
      if (err instanceof ApiError && err.code === "product_incomplete") setMissing(err.fields ?? {});
      else void message.error(errorText(err));
    },
  });
  const check = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/products/{productID}/channels/{connectionID}/check", { params: { path: { productID: product.id, connectionID } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });
  const uploadChart = useMutation({
    mutationFn: (f: File) =>
      unwrap(
        api.POST("/v1/products/{productID}/images", {
          params: { path: { productID: product.id }, query: { role: "size_chart" } },
          body: f as unknown as string,
          bodySerializer: (b: unknown) => b as Blob,
          headers: { "Content-Type": f.type },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["products", "detail", product.id] }),
    onError: (err) => void message.error(errorText(err)),
  });

  const attrName = (id: string) => terms.data?.attributes.find((a) => a.id === id)?.name ?? id;
  const missingText = (key: string) => {
    if (key.startsWith("attributes.")) return t("prod.missing.attribute", { name: attrName(key.slice(11)) });
    const v = /^variants\[(\d+)\]\.(\w+)$/.exec(key);
    if (v) return t(`prod.missing.variant.${v[2] === "price" ? "price" : "sku"}`, { sku: product.variants[Number(v[1])]?.skuCode ?? "" });
    const known = ["categoryId", "images", "weightG", "dimensions", "sizeChartId", "variants"] as const;
    return (known as readonly string[]).includes(key) ? t(`prod.missing.${key as (typeof known)[number]}`) : key;
  };
  const sizeChart = product.images.find((i) => i.role === "size_chart");
  const rules = terms.data?.rules;
  const published = !!ch?.externalId;

  return (
    <Drawer
      open={!!store}
      onClose={onClose}
      size="large"
      title={
        store && (
          <Space>
            <PlatformLabel platform={store.platform} />
            <span>{store.name}</span>
            {ch && <ChannelStatusTag status={ch.status} />}
          </Space>
        )
      }
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => form.validateFields().then((v) => save.mutate(v))} loading={save.isPending}>
            {t("common.save")}
          </Button>
          <Button type="primary" icon={<SendOutlined />} onClick={() => form.validateFields().then((v) => publish.mutate(v))} loading={publish.isPending}>
            {t(published ? "prod.store.update" : "prod.store.publish")}
          </Button>
        </Space>
      }
      destroyOnHidden
    >
      {ch && ch.status !== "not_published" && (
        <Space orientation="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label={t("prod.store.status")}>
              <Space>
                <ChannelStatusTag status={ch.status} />
                {ch.platformStatus && <Typography.Text type="secondary" className="z-num">{ch.platformStatus}</Typography.Text>}
                {published && (
                  <Button size="small" icon={<ReloadOutlined />} loading={check.isPending} onClick={() => check.mutate()}>
                    {t("prod.store.check")}
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
            {ch.externalId && (
              <Descriptions.Item label={t("prod.store.externalId")}>
                <span className="z-num">{ch.externalId}</span>
              </Descriptions.Item>
            )}
            {ch.pushedAt && (
              <Descriptions.Item label={t("prod.store.pushed")}>
                {ch.pushedName} · {formatDateTime(ch.pushedAt, tz)}
              </Descriptions.Item>
            )}
          </Descriptions>
          {ch.status === "in_review" && <Alert type="info" showIcon title={t("prod.store.inReview")} />}
          {ch.rejections.length > 0 && (
            <Alert
              type="error"
              showIcon
              title={t("prod.store.rejected")}
              action={
                onFix && (
                  <Button size="small" icon={<ExperimentOutlined />} onClick={() => onFix(ch.connectionId)}>
                    {t("prod.ai.fix")}
                  </Button>
                )
              }
              description={
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {ch.rejections.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              }
            />
          )}
          {ch.lastError && <Alert type="error" showIcon title={t("prod.store.lastError")} description={ch.lastError} />}
          {ch.warnings.length > 0 && <Alert type="warning" showIcon title={t("prod.store.warnings")} description={ch.warnings.join(" · ")} />}
        </Space>
      )}
      {missing && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("prod.missing.title")}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {Object.keys(missing).map((k) => (
                <li key={k}>{missingText(k)}</li>
              ))}
            </ul>
          }
        />
      )}
      {categories.isError && <Alert type="error" showIcon style={{ marginBottom: 16 }} title={errorText(categories.error)} />}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item label={t("prod.store.category")} required extra={!categoryId ? t("prod.store.categoryHelp") : undefined}>
          <Flex gap={8}>
            <Form.Item name="category" noStyle rules={[{ required: true }]}>
              <Cascader
                options={tree}
                showSearch={{ filter: (input, options) => options.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }}
                placeholder={t("prod.store.pickCategory")}
                loading={categories.isFetching}
                style={{ flex: 1 }}
              />
            </Form.Item>
            <Button icon={<BulbOutlined />} loading={recommend.isPending} onClick={() => recommend.mutate()} disabled={cats.length === 0}>
              {t("prod.store.recommend")}
            </Button>
          </Flex>
        </Form.Item>
        {categoryId && (
          <>
            <Form.Item name="brandId" label={t("prod.store.brand")} extra={t("prod.store.brandHelp")}>
              <Select
                allowClear
                showSearch={{ filterOption: false, onSearch: setBrandQ }}
                loading={brands.isFetching}
                placeholder={t("prod.store.noBrand")}
                options={[
                  ...(ch?.brandId && !(brands.data?.items ?? []).some((b) => b.id === ch.brandId) ? [{ value: ch.brandId, label: ch.brandName }] : []),
                  ...(brands.data?.items ?? []).map((b) => ({ value: b.id, label: b.name })),
                ]}
              />
            </Form.Item>
            <Typography.Title level={5}>{t("prod.store.attributes")}</Typography.Title>
            {terms.isFetching && !terms.data && <Typography.Text type="secondary">…</Typography.Text>}
            {(terms.data?.attributes ?? [])
              .filter((a) => !a.sales)
              .sort((a, b) => Number(b.required) - Number(a.required))
              .map((a) => (
                <Form.Item
                  key={a.id}
                  name={["attrs", a.id]}
                  label={a.name}
                  rules={a.required ? [{ required: true, message: t("prod.store.attrRequired") }] : undefined}
                  required={a.required}
                >
                  <Select
                    allowClear
                    mode={a.customizable ? "tags" : a.multiple ? "multiple" : undefined}
                    maxCount={a.multiple ? undefined : 1}
                    showSearch={{ optionFilterProp: "label" }}
                    options={a.values.map((v) => ({ value: v.id, label: v.name }))}
                    placeholder={a.customizable ? t("prod.store.attrCustom") : undefined}
                  />
                </Form.Item>
              ))}
            {terms.data && (terms.data.attributes ?? []).some((a) => a.sales) && (
              <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                {t("prod.store.salesAttrs", {
                  names: terms.data.attributes
                    .filter((a) => a.sales)
                    .map((a) => a.name)
                    .join(", "),
                })}
              </Typography.Paragraph>
            )}
            {rules?.sizeChartSupported && (
              <Form.Item label={t("prod.store.sizeChart")} required={rules.sizeChartRequired} extra={t("prod.store.sizeChartHelp")}>
                <Space align="start">
                  {sizeChart && <ProductImage productId={product.id} imageId={sizeChart.id} size={88} />}
                  <Upload
                    accept="image/jpeg,image/png"
                    showUploadList={false}
                    beforeUpload={(f) => {
                      uploadChart.mutate(f);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} loading={uploadChart.isPending}>
                      {t(sizeChart ? "prod.store.replaceChart" : "prod.store.uploadChart")}
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
            )}
            {rules?.codSupported !== false && (
              <Form.Item name="cod" label={t("prod.store.cod")} valuePropName="checked">
                <Switch />
              </Form.Item>
            )}
          </>
        )}
        <Typography.Title level={5}>{t("prod.store.prices")}</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t("prod.store.pricesHelp")}
        </Typography.Paragraph>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={product.variants}
          columns={[
            {
              title: t("prod.variant"),
              render: (_, v) => (
                <span>
                  <Typography.Text strong className="z-num">
                    {v.skuCode}
                  </Typography.Text>
                  {v.options.length > 0 && <Typography.Text type="secondary"> · {v.options.join(" / ")}</Typography.Text>}
                </span>
              ),
            },
            {
              title: t("prod.basePrice"),
              width: 150,
              align: "right",
              render: (_, v) => {
                const base = v.price ?? product.price;
                return base != null ? <span className="z-num">{formatMoney(base)}</span> : <Typography.Text type="danger">{t("prod.noPrice")}</Typography.Text>;
              },
            },
            {
              title: t("prod.store.price"),
              width: 180,
              render: (_, v) => (
                <Form.Item name={["prices", v.id]} noStyle>
                  <InputNumber min={1} precision={0} prefix="Rp" placeholder={t("prod.store.samePrice")} style={{ width: 160 }} />
                </Form.Item>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
}
